// Trip resolution for /windows and /regions, which previously demanded a hand-typed
// ?trip= and were therefore unreachable. /intake keeps its own copy for now because its
// gate also loads the household and role in the same pass.

import { useEffect, useMemo, useState, type JSX } from 'react'

import { supabase } from './supabaseClient'
import { useAuth } from './auth'
import { decideTrip, type TripOption } from './resolveTrip'

export type ResolvedTrip =
  | { kind: 'loading' }
  | { kind: 'ready'; tripId: string }
  | { kind: 'none' }
  | { kind: 'choose'; trips: TripOption[] }
  | { kind: 'error'; message: string }

export type UnresolvedTrip = Exclude<ResolvedTrip, { kind: 'ready' }>

export function useResolvedTrip(): ResolvedTrip {
  const { user } = useAuth()
  const userId = user?.id ?? null
  const paramTrip = useMemo(() => new URLSearchParams(window.location.search).get('trip'), [])

  const [state, setState] = useState<ResolvedTrip>(() =>
    paramTrip ? { kind: 'ready', tripId: paramTrip } : { kind: 'loading' },
  )

  useEffect(() => {
    if (paramTrip || !userId) return
    let cancelled = false

    void (async () => {
      const { data: rows, error } = await supabase
        .from('membership')
        .select('trip_id')
        .eq('user_id', userId)
      if (cancelled) return
      if (error) {
        setState({ kind: 'error', message: 'We could not look up your trips.' })
        return
      }

      const ids = (rows ?? []).map((r) => (r as { trip_id: string }).trip_id)
      const first = decideTrip({ paramTrip: null, membershipTripIds: ids })

      // Only pay for the names when a choice actually has to be offered.
      if (first.kind !== 'choose') {
        setState(first)
        return
      }

      const { data: trips } = await supabase
        .from('trip')
        .select('id, name')
        .in(
          'id',
          first.trips.map((t) => t.id),
        )
      if (cancelled) return

      const names = new Map(
        ((trips ?? []) as { id: string; name: string }[]).map((t) => [t.id, t.name]),
      )
      setState(decideTrip({ paramTrip: null, membershipTripIds: ids, names }))
    })()

    return () => {
      cancelled = true
    }
  }, [paramTrip, userId])

  return state
}

/** The not-ready screens, so /windows and /regions word them identically. */
export function TripGateFallback({
  resolution,
  what,
}: {
  resolution: UnresolvedTrip
  what: string
}): JSX.Element {
  if (resolution.kind === 'loading') {
    return (
      <main className="spz-shell">
        <p className="spz-sub">Finding your trip&hellip;</p>
      </main>
    )
  }

  if (resolution.kind === 'error') {
    return (
      <main className="spz-shell">
        <h1>Something went wrong</h1>
        <p role="alert">{resolution.message}</p>
      </main>
    )
  }

  if (resolution.kind === 'none') {
    return (
      <main className="spz-shell">
        <h1>No trip yet</h1>
        <p>
          You&rsquo;re signed in, but you&rsquo;re not on a trip yet, so there are no {what} to
          show.
        </p>
        <p>
          <a href="/join">Find my invitation</a>
        </p>
      </main>
    )
  }

  return (
    <main className="spz-shell">
      <h1>Which trip?</h1>
      <p className="spz-sub">You&rsquo;re on more than one. Pick the one you want {what} for.</p>
      <ul>
        {resolution.trips.map((t) => (
          <li key={t.id}>
            <a href={`?trip=${encodeURIComponent(t.id)}`}>{t.name}</a>
          </li>
        ))}
      </ul>
    </main>
  )
}
